import type { LandingPageProps } from '@/components/landing-page/landing-page.types';
import { useEffect, useState } from 'react';
import { useBoolean, useWindowSize } from 'usehooks-ts';
import Dialog from '@/components/dialog';
import Quiz from '@/components/quiz';
import Loader from '@/components/landing-page/components/loader';
import Sidebar from '@/components/landing-page/components/sidebar';
import Footer from '@/components/landing-page/components/footer';
import Main from '@/components/landing-page/components/main';
import Parser from '@/components/landing-page/utils/parser';
import useIsSidebarSticky from '@/components/landing-page/hooks/useIsSidebarSticky';
import useHideElementsPostQid from '@/components/landing-page/hooks/useHideElementsPostQid';
import useLoadClientSecret from '@/components/landing-page/hooks/useLoadClientSecret';
import usePriorityVersion from '@/components/landing-page/hooks/usePriorityVersion';
import useStickySuccessBox from '@/components/landing-page/hooks/useStickySuccessBox';
import { useAppDispatch, useAppSelector } from '@/redux/store';
import { ConfigSlice } from '@/redux/config';
import { GeneralSlice } from '@/redux/general';
import { SliderSlice } from '@/redux/slider';
import { cn } from '@/utils/helper';
import v1 from '@/components/landing-page/landing-page-v1.module.css';
import v2 from '@/components/landing-page/landing-page-v2.module.css';

export default function LandingPage(props: LandingPageProps) {
  const { version } = props;
  const dispatch = useAppDispatch();
  const [loaded, setLoaded] = useState<boolean>(false);
  const { value: isQuizOpen, setTrue: openQuiz, setFalse: closeQuiz } = useBoolean(false);
  const { width } = useWindowSize();
  const mappedAnswers = useAppSelector(SliderSlice.selectors.mappedAnswers);
  const dialog = useAppSelector(ConfigSlice.selectors.additionalQuestions);
  const isBrazeProspect = useAppSelector(GeneralSlice.selectors.brazeProspect);
  const prio = usePriorityVersion(version);
  const status = useLoadClientSecret(version);
  const isSidebarSticky = useIsSidebarSticky(loaded, width);
  useStickySuccessBox(isSidebarSticky);
  useHideElementsPostQid(status);

  const { page } = new Parser(prio, mappedAnswers, width);

  useEffect(() => {
    setLoaded(status === 'success');
  }, [status]);

  useEffect(() => {
    dispatch(GeneralSlice.actions.setPage(page.sidebar.length ? 'landing-page' : 'landing-page-no-sidebar'));
    return () => {
      dispatch(GeneralSlice.actions.setPage('slider'));
    };
  }, [page.sidebar.length]);

  useEffect(() => {
    if (!dialog?.show || isBrazeProspect) return;
    const timer = setTimeout(openQuiz, 0);
    return () => clearTimeout(timer);
  }, [dialog, isBrazeProspect]);

  return (
    <div className={cn(v1.LandingPage, v2.LandingPage)}>
      {loaded ? (
        <>
          <div className={cn(v1.Body, v2.Body)}>
            <div className={cn(v1.Main, v2.Main)}>
              <Main successBox={page.successBox} groups={page.question} />
            </div>
            {page.sidebar.length ? (
              <div className={cn(v1.Sidebar, v2.Sidebar)}>
                <Sidebar groups={page.sidebar} />
              </div>
            ) : null}
          </div>
          {page.footer.length ? (
            <div className={cn(v1.Footer, v2.Footer)}>
              <Footer groups={page.footer} />
            </div>
          ) : null}
        </>
      ) : (
        <Loader className={cn(v1.Loader, v2.Loader)} />
      )}
      <Dialog on={isQuizOpen} size='2/3' position='center' heightMax='70' onClose={closeQuiz}>
        <Quiz onCompleteQuiz={closeQuiz} />
      </Dialog>
    </div>
  );
}
